import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BellIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export interface ShellNavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  current: boolean;
}

interface AppShellProps {
  brandTitle: string;
  brandSubtitle: string;
  brandIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  navigation: ShellNavItem[];
  userName?: string;
  userSubtitle?: string;
  userCaption?: string;
  roleBadge?: string;
  headerExtras?: React.ReactNode;
  onLogout: () => void;
  children: React.ReactNode;
}

const getInitials = (name?: string) => name?.trim().charAt(0) || '؟';

const formatToday = () => {
  const now = new Date();
  try {
    const gregorian = new Intl.DateTimeFormat('ar-u-ca-gregory-nu-latn', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(now);
    const hijri = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura-nu-latn', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(now);
    return `${gregorian} · ${hijri}`;
  } catch {
    return now.toLocaleDateString('ar');
  }
};

const AppShell: React.FC<AppShellProps> = ({
  brandTitle,
  brandSubtitle,
  brandIcon: BrandIcon,
  navigation,
  userName,
  userSubtitle,
  userCaption,
  roleBadge,
  headerExtras,
  onLogout,
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const currentTitle = navigation.find((item) => item.current)?.name || 'لوحة التحكم';

  // Close the mobile drawer on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const sidebarContent = (
    <div className="flex h-full flex-col overflow-hidden border-l border-gray-200 bg-white">

      {/* Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-gray-100 px-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50">
          <BrandIcon className="h-5 w-5 text-primary-700" />
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-base font-bold leading-tight text-gray-900">{brandTitle}</h1>
          <p className="truncate text-xs text-gray-500">{brandSubtitle}</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {navigation.map((item) => (
          <Link
            key={item.href + item.name}
            to={item.href}
            className={`sidebar-item ${item.current ? 'active' : ''}`}
            aria-current={item.current ? 'page' : undefined}
          >
            <item.icon className={`h-5 w-5 shrink-0 ${item.current ? 'text-primary-600' : 'text-gray-400'}`} />
            <span className="truncate">{item.name}</span>
          </Link>
        ))}
      </nav>

      {/* User card */}
      <div className="border-t border-gray-100 p-3">
        <div className="flex items-center gap-3 px-2 py-1">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
            {getInitials(userName)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{userName || 'مستخدم'}</p>
            {userSubtitle && <p className="truncate text-xs text-gray-500">{userSubtitle}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600"
        >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            تسجيل الخروج
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 rtl">
      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!sidebarOpen}
      >
        <div
          className={`absolute inset-0 bg-gray-900/40 transition-opacity duration-200 ${
            sidebarOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={`absolute inset-y-0 right-0 w-72 max-w-[85%] transform transition-transform duration-200 ${
            sidebarOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {sidebarContent}
          <button
            type="button"
            className="absolute top-3.5 left-3 flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
            onClick={() => setSidebarOpen(false)}
            aria-label="إغلاق القائمة"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden md:fixed md:inset-y-0 md:right-0 md:z-30 md:flex md:w-64 md:flex-col">
        {sidebarContent}
      </aside>

      {/* Main column */}
      <div className="flex min-h-screen flex-col md:pr-64">
        <header className="sticky top-0 z-20 border-b border-gray-200 bg-white">
          <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 md:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label="فتح القائمة"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-lg font-semibold text-gray-900">{currentTitle}</h2>
                  {roleBadge && (
                    <span className="badge hidden bg-gray-100 text-gray-600 sm:inline-flex">
                      {roleBadge}
                    </span>
                  )}
                </div>
                <p className="hidden truncate text-xs text-gray-500 sm:block">{formatToday()}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {headerExtras}
              <button
                type="button"
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                aria-label="الإشعارات"
              >
                <BellIcon className="h-6 w-6" />
              </button>
              <div className="hidden items-center gap-3 border-r border-gray-200 pr-3 sm:flex">
                <div className="leading-tight">
                  <p className="text-sm font-medium text-gray-900">{userName}</p>
                  {userCaption && <p className="text-xs text-gray-500">{userCaption}</p>}
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                  {getInitials(userName)}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AppShell;
